import { Box, Container } from '@mui/material';
import { useLocalStorage } from '@uidotdev/usehooks';
import * as date from 'date-fns';
import { useState } from 'react';
import { RouteObject } from 'react-router-dom';
import useSWR from 'swr';
import { PageSpinner, Video } from '~/components';
import { ChartCard } from '~/components/ChartCard';
import { PageHeader } from '~/components/PageHeader';
import { useFarm } from '~/hooks/useFarm';
import { applyStandardDeviation1SD,applyStandardDeviationpoint5SD } from '~/utils/stddev';
import birdcountavgCsvRaw from './PlayPastData/DAY_MEAN_BIRDCOUNT.csv?raw';
import birdcountstddevCsvRaw from './PlayPastData/DAY_SD1_BIRDCOUNT.csv?raw';
import clusteringavgCsvRaw from './PlayPastData/DAY_MEAN_PLAYCLUSTERING.csv?raw';
import clusteringstddevCsvRaw from './PlayPastData/DAY_SD1_PLAYCLUSTERING.csv?raw';
import lowactivityCsvRaw from './PlayPastData/DAY_MEAN_PLAYLOWTRAJ.csv?raw';
import lowactivitystddevCsvRaw from './PlayPastData/DAY_SD1_PLAYLOWTRAJ.csv?raw';
import moderateactivityavgCsvRaw from './PlayPastData/DAY_MEAN_PLAYMODTRAJ.csv?raw';
import moderateactivitystddevCsvRaw from './PlayPastData/DAY_SD1_PLAYMODTRAJ.csv?raw';
import highactivityavgCsvRaw from './PlayPastData/DAY_MEAN_PLAYHIGHTRAJ.csv?raw';
import highactivitystddevCsvRaw from './PlayPastData/DAY_SD1_PLAYHIGHTRAJ.csv?raw';
import birddensityavgCsvRaw from './PlayPastData/DAY_MEAN_PLAYDENSITY.csv?raw';
import birddensitystddevCsvRaw from './PlayPastData/DAY_SD1_PLAYDENSITY.csv?raw';
export const playRoute: RouteObject = {
  path: '/play',
  element: <Play />,
}

const defaultFrom = date.subWeeks(new Date(), 6)
const defaultTo = date.addDays(new Date(), 1)

function Play() {
  const { farm, isLoading: isLoadingFarm, accessStartDate } = useFarm()

  const [from, setFrom] = useLocalStorage('settings.from', date.formatISO(defaultFrom, { representation: 'date' }))
  const [to, setTo] = useLocalStorage('settings.to', date.formatISO(defaultTo, { representation: 'date' }))
  const [params, setParams] = useState({ from, to })
  
  // Convert access start date to Date object for date picker minDate
  const minDate = accessStartDate ? date.parseISO(accessStartDate) : null

  const { data: feederData = [], isLoading } = useSWR(
    farm?.id ? `/farms/${farm.id}/insights/play?dateFrom=${params.from}&dateTo=${params.to}` : null)

  const { data: activityMedia } = useSWR(
    farm?.id ? `/farms/${farm.id}/media/play-activity/${params.to.replace(/-/g, '')}` : null)

  const { data: heatmapMedia } = useSWR(
    farm?.id ? `/farms/${farm.id}/media/play-heatmap/${params.to.replace(/-/g, '')}` : null)

  const parseNumberLines = (text: string): number[] =>
  text
    .trim()
    .split(/\r?\n/)
    .map(line => Number(line.trim()))
    .filter(n => !Number.isNaN(n)); 



function computeCycleLength(birdAges: number[]): number {
  let maxBeforeReset = 0;

  for (let i = 0; i < birdAges.length; i++) {
    const age = birdAges[i];

    // When we hit a new cycle start (age === 1), we stop reading previous cycle
    if (age === 1 && i !== 0) break;

    if (age > maxBeforeReset) maxBeforeReset = age;
  }

  return maxBeforeReset;
}

const birdCountPastAvg = parseNumberLines(birdcountavgCsvRaw);
const birdCountPastSd = parseNumberLines( birdcountstddevCsvRaw);

const densityPastAvg = parseNumberLines(birddensityavgCsvRaw);
const densityPastSd = parseNumberLines(birddensitystddevCsvRaw);

const clusteringPastAvg = parseNumberLines(clusteringavgCsvRaw);
const clusteringPastSd = parseNumberLines(clusteringstddevCsvRaw);

const lowPastAvg = parseNumberLines(lowactivityCsvRaw);
const lowPastSd = parseNumberLines(lowactivitystddevCsvRaw);

const moderatePastAvg = parseNumberLines(moderateactivityavgCsvRaw);
const moderatePastSd = parseNumberLines(moderateactivitystddevCsvRaw);

const highPastAvg = parseNumberLines(highactivityavgCsvRaw);
const highPastSd = parseNumberLines(highactivitystddevCsvRaw);



 
// cycle based on live ages 
const ages = feederData.map((d: any) => Number(d.birdage ?? 0));
const CYCLE = computeCycleLength(ages);

// safety fallback 
const cycleSafe = CYCLE > 0 ? CYCLE : Math.max(
  birdCountPastAvg.length,
  densityPastAvg.length,
  lowPastAvg.length,
  1
);

const isMoyParkFarm = farm?.id === "experiment-moypark";



const AGE_INVALID = Number.MAX_SAFE_INTEGER;
const birdCount = feederData.map((d: any) => {
  const birdage = Number(d.birdage ?? d.birdAge ?? d.age);

  const wrappedAge =
    Number.isFinite(birdage) && birdage > 0 && isMoyParkFarm
      ? ((birdage - 1) % cycleSafe) + 1
      : AGE_INVALID;

  const idx = wrappedAge - 1;
   //  if wrappedAge exceeds CSV range,do not render any past data
   const outOfRange = idx < 0 || idx >= birdCountPastAvg.length;
    const pastavg = Math.max(0,(birdCountPastAvg[idx] ?? 0));
  const realstdDev = birdCountPastSd[idx] ?? 0;

  
  if (outOfRange) {
    return {
      datetime: date.addHours(date.parseISO(d.date), d.hour),
      date: d.date,
      hour: d.hour,
      pastavg: null,
      avg: d.birdCountAvg,
      age: wrappedAge,
      stddev: null,
      stddevp5: null
    };
  }
 

  return {
    datetime: date.addHours(date.parseISO(d.date), d.hour),
    pastavg ,
    avg:d.birdCountAvg,
    age:wrappedAge,
    stddev: applyStandardDeviation1SD(pastavg, realstdDev),
    stddevp5: applyStandardDeviationpoint5SD(pastavg, realstdDev),
 
  };
});

  const clustering = feederData.map((d: any) => {
      const birdage = Number(d.birdage ?? d.birdAge ?? d.age);

  const wrappedAge =
    Number.isFinite(birdage) && birdage > 0 && isMoyParkFarm
      ? ((birdage - 1) % cycleSafe) + 1
      : AGE_INVALID;

  const idx = wrappedAge - 1;
   //  if wrappedAge exceeds CSV range,do not render any past data
   const outOfRange = idx < 0 || idx >= birdCountPastAvg.length;
   const pastavg = Math.max(0, (clusteringPastAvg[idx] ?? 0));
     const realstdDev = clusteringPastSd[idx] ?? 0;


  
  if (outOfRange) {
    return {
      datetime: date.addHours(date.parseISO(d.date), d.hour),
      date: d.date,
      hour: d.hour,
      pastavg: null,
      avg: d.clusteringAvg,
      age: wrappedAge,
      stddev: null,
      stddevp5: null
    };
  }
    
    return {
      datetime: date.addHours(date.parseISO(d.date), d.hour),
      pastavg ,
      avg:d.clusteringAvg,
       age:wrappedAge,
      stddev: applyStandardDeviation1SD(pastavg, realstdDev),
      stddevp5: applyStandardDeviationpoint5SD(pastavg, realstdDev),
   
    }
  })

  const lowActivity = feederData.map((d: any) => {
     const birdage = Number(d.birdage ?? d.birdAge ?? d.age);

  const wrappedAge =
    Number.isFinite(birdage) && birdage > 0 && isMoyParkFarm
      ? ((birdage - 1) % cycleSafe) + 1
      : AGE_INVALID;
  const idx = wrappedAge - 1;

   //  if wrappedAge exceeds CSV range,do not render any past data
   const outOfRange = idx < 0 || idx >= birdCountPastAvg.length;
   const pastavg = Math.max(0,(lowPastAvg[idx] ?? 0));
   const realstdDev = lowPastSd[idx] ?? 0;
     

  
  if (outOfRange) {
    return {
      datetime: date.addHours(date.parseISO(d.date), d.hour),
      date: d.date,
      hour: d.hour,
      pastavg: null,
      avg: d.lowActivityAvg,
      age: wrappedAge,
      stddev: null,
      stddevp5: null
    };
  }

    return {
       datetime: date.addHours(date.parseISO(d.date), d.hour),
       pastavg ,
       avg:d.lowActivityAvg,
      age:wrappedAge,
       stddev: applyStandardDeviation1SD(pastavg, realstdDev),
       stddevp5: applyStandardDeviationpoint5SD(pastavg, realstdDev),
   
    }
  })

  const moderateActivity = feederData.map((d: any) => {
     const birdage = Number(d.birdage ?? d.birdAge ?? d.age);

  const wrappedAge =
    Number.isFinite(birdage) && birdage > 0 && isMoyParkFarm
      ? ((birdage - 1) % cycleSafe) + 1
      : AGE_INVALID;

  const idx = wrappedAge - 1;
   //  if wrappedAge exceeds CSV range,do not render any past data
   const outOfRange = idx < 0 || idx >= birdCountPastAvg.length;
    const pastavg = Math.max(0,(moderatePastAvg[idx] ?? 0));
     const realstdDev = moderatePastSd[idx] ?? 0;
     

  
  if (outOfRange) {
    return {
      datetime: date.addHours(date.parseISO(d.date), d.hour),
      date: d.date,
      hour: d.hour,
      pastavg: null,
      avg: d.moderateActivityAvg,
      age: wrappedAge,
      stddev: null,
      stddevp5: null
    };
  }


    return {
       datetime: date.addHours(date.parseISO(d.date), d.hour),
       pastavg ,
       avg:d.moderateActivityAvg ?? 0,
       age:wrappedAge,
       stddev: applyStandardDeviation1SD(pastavg, realstdDev),
       stddevp5: applyStandardDeviationpoint5SD(pastavg, realstdDev),
    }
  })

  const highActivity = feederData.map((d: any) => {
     const birdage = Number(d.birdage ?? d.birdAge ?? d.age);

  const wrappedAge =
    Number.isFinite(birdage) && birdage > 0 && isMoyParkFarm
      ? ((birdage - 1) % cycleSafe) + 1
      : AGE_INVALID;

  const idx = wrappedAge - 1;
  //  if wrappedAge exceeds CSV range,do not render any past data
    const outOfRange = idx < 0 || idx >= birdCountPastAvg.length;
     const pastavg = Math.max(0,(highPastAvg[idx] ?? 0));
     const realstdDev = highPastSd[idx] ?? 0;

  
  if (outOfRange) {
    return {
      datetime: date.addHours(date.parseISO(d.date), d.hour),
      date: d.date,
      hour: d.hour,
      pastavg: null,
      avg: d.highActivityAvg,
      age: wrappedAge,
      stddev: null,
      stddevp5: null
    };
  }
    

    return {
       datetime: date.addHours(date.parseISO(d.date), d.hour),
       pastavg ,
        avg:d.highActivityAvg ?? 0,
        age:wrappedAge,
       stddev: applyStandardDeviation1SD(pastavg, realstdDev),
       stddevp5: applyStandardDeviationpoint5SD(pastavg, realstdDev),
    }
  })

  const birdDensity = feederData.map((d: any) => {
     const birdage = Number(d.birdage ?? d.birdAge ?? d.age);

  const wrappedAge =
    Number.isFinite(birdage) && birdage > 0 && isMoyParkFarm
      ? ((birdage - 1) % cycleSafe) + 1
      : AGE_INVALID;
      

  const idx = wrappedAge - 1;
  //  if wrappedAge exceeds CSV range,do not render any past data
      const outOfRange = idx < 0 || idx >= birdCountPastAvg.length;
       const pastavg = Math.max(0,(densityPastAvg[idx] ?? 0));
     const realstdDev = densityPastSd[idx] ?? 0;

  
  if (outOfRange) {
    return {
      datetime: date.addHours(date.parseISO(d.date), d.hour),
      date: d.date,
      hour: d.hour,
      pastavg: null,
      avg:d.birdDensityAvg,
      age: wrappedAge,
      stddev: null,
      stddevp5: null
    };
  }

   
    return {
      datetime: date.addHours(date.parseISO(d.date), d.hour),
      pastavg ,
      avg:d.birdDensityAvg,
       age:wrappedAge,
      stddev: applyStandardDeviation1SD(pastavg, realstdDev),
      stddevp5: applyStandardDeviationpoint5SD(pastavg, realstdDev),
    }
  })

  const handleApply = () => {
    setParams({ from, to })
  }


  return (
    <Container maxWidth={false} sx={{ pb: '24px' }}>
      <PageHeader title="Play" from={from} to={to} setFrom={setFrom} setTo={setTo} handleApply={handleApply} minDate={minDate} />
      {(isLoadingFarm || isLoading) && <PageSpinner />}
      {!isLoadingFarm && !isLoading && (
        <Box display={"grid"} gridTemplateColumns={"repeat(auto-fill, minmax(500px, 700px))"} gap={2}>
          {activityMedia?.url && <Video height='375px' width='100%' src={activityMedia.url} />}
          {heatmapMedia?.url && <Video height='375px' width='100%' src={heatmapMedia.url} />}
          <ChartCard title="Bird count" data={birdCount} />
          <ChartCard title="Clustering" data={clustering} />
          <ChartCard title="Low Activity" data={lowActivity} />
          <ChartCard title="Moderate Activity" data={moderateActivity} />
          <ChartCard title="High Activity" data={highActivity} />
          <ChartCard title="Bird Density" data={birdDensity} />
        </Box>
      )}

    </Container>
  )
}
